import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import IconSvg, { Icons } from 'src/assets/icons/IconSvg';
import Button from 'src/components/button/Button';
import IconButton from 'src/components/button/IconButton';
import ToggleButton from 'src/components/button/ToggleButton';
import RaffleCard from 'src/components/cards/RaffleCard';
import NumberInputWithMultiplier from 'src/components/input/NumberInputWithMultiplier';
import Pagination from 'src/components/common/Pagination';
import SelectWalletModal from 'src/components/modal/SelectWalletModal';
import ReportRaffleModal from 'src/components/modal/ReportRaffleModal';
import { useViewport } from 'src/context/ViewportContext';
import { useWallet, useNotification } from 'src/hooks';
import { useAuth } from 'src/hooks/useAuth';
import { useCountdown } from 'src/hooks/useCountdown';
import { useRaffleDetail } from 'src/hooks/useRaffleDetail';
import { useRaffleParticipants } from 'src/hooks/useRaffleParticipants';
import { useRaffleActions } from 'src/hooks/useRaffleActions';
import { useRaffles } from 'src/hooks/useRaffles';
import { useCart } from 'src/contexts/cart-context';
import {
  getRaffleDisplayName,
  getRaffleImage,
  getRaffleCreator,
  formatTicketsSold,
  satsToBtc,
} from 'src/utils/raffleHelpers';
import type { Raffle } from 'src/types/raffle';

const tabs: { label: string; icon: Icons; key: string }[] = [
  { label: 'Participants', icon: 'usersS', key: 'participants' },
  { label: 'Transactions', icon: 'transactionS', key: 'transactions' },
];

function pickRandom<T>(arr: T[], count: number): T[] {
  const copy = [...arr];
  const result: T[] = [];
  for (let i = 0; i < count && copy.length > 0; i++) {
    const idx = Math.floor(Math.random() * copy.length);
    result.push(copy.splice(idx, 1)[0]);
  }
  return result;
}

const JoinRaffle = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isMobile } = useViewport();
  const notify = useNotification();
  const { isConnected, signMessage } = useWallet();
  const { userId } = useAuth();

  // Data hooks
  const { raffle, isLoading: raffleLoading, fetchRaffle } = useRaffleDetail();
  const {
    participants,
    participantsMeta,
    transactions,
    transactionsMeta,
    isLoadingParticipants,
    isLoadingTransactions,
    fetchParticipants,
    fetchTransactions,
  } = useRaffleParticipants();
  const {
    buyTickets,
    toggleFavorite,
    reportRaffle,
    getWinners,
    getUserTicketCount,
    isLoading: actionLoading,
  } = useRaffleActions();
  const { addToCart } = useCart();

  // Recommendations
  const { raffles: recommendedRaffles, fetchRaffles: fetchRecommended } = useRaffles();

  // Local state
  const [enabled, setEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState('participants');
  const [quantity, setQuantity] = useState(1);
  const [userTickets, setUserTickets] = useState(0);
  const [isFavorited, setIsFavorited] = useState(false);
  const [winners, setWinners] = useState<any[]>([]);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [participantsPage, setParticipantsPage] = useState(1);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [displayRecommended, setDisplayRecommended] = useState<Raffle[]>([]);
  const [copied, setCopied] = useState(false);

  const timeLeft = useCountdown(raffle?.endDate ?? '');

  // Fetch raffle on mount
  useEffect(() => {
    if (id) fetchRaffle(id);
  }, [id]);

  // Fetch user tickets, winners, and recommendations when raffle loads
  useEffect(() => {
    if (!raffle) return;

    if (userId && raffle.id) {
      getUserTicketCount(raffle.id).then(setUserTickets);
    }

    if (raffle.status === 'ENDED' || raffle.status === 'COMPLETED') {
      getWinners(raffle.id).then(setWinners);
    }

    fetchRecommended({ status: 'LIVE', limit: 20 });
  }, [raffle?.id, userId]);

  // Fetch participants/transactions when tab or page changes
  useEffect(() => {
    if (!raffle?.id) return;
    if (activeTab === 'participants') {
      fetchParticipants(raffle.id, participantsPage);
    } else {
      fetchTransactions(raffle.id, transactionsPage);
    }
  }, [raffle?.id, activeTab, participantsPage, transactionsPage]);

  // Pick random recommendations excluding current raffle
  useEffect(() => {
    if (recommendedRaffles.length === 0 || !raffle) return;
    const filtered = recommendedRaffles.filter(r => r.id !== raffle.id);
    const ordinals = filtered.filter(r => r.prizeType === 'ORDINALS');
    const runes = filtered.filter(r => r.prizeType === 'RUNES');
    const picked: Raffle[] = [];
    picked.push(...pickRandom(ordinals, 2));
    picked.push(...pickRandom(runes, 2));
    if (picked.length < 4) {
      const remaining = filtered.filter(r => !picked.includes(r));
      picked.push(...pickRandom(remaining, 4 - picked.length));
    }
    setDisplayRecommended(picked);
  }, [recommendedRaffles, raffle?.id]);

  const goBack = () => navigate(-1);

  const isTimePassed = raffle ? new Date() >= new Date(raffle.endDate) : false;
  const isRaffleLive = raffle?.status === 'LIVE' && !isTimePassed;
  const isRaffleEnded = raffle?.status === 'ENDED' || raffle?.status === 'COMPLETED';
  const isAwaitingWinner = raffle?.status === 'LIVE' && isTimePassed;
  const isCreator = userId === raffle?.creatorId;
  const remainingTickets = raffle ? raffle.supply - raffle.ticketsSold : 0;
  const maxQuantity = raffle
    ? Math.min(
        raffle.ticketLimitPerWallet
          ? raffle.ticketLimitPerWallet - userTickets
          : remainingTickets,
        remainingTickets,
      )
    : 0;
  const canBuy = isConnected && isRaffleLive && !isCreator && maxQuantity > 0 && quantity > 0;
  const totalBtc = raffle ? satsToBtc(raffle.ticketPriceSats) * quantity : 0;
  const probability =
    raffle && raffle.ticketsSold > 0
      ? ((userTickets / raffle.ticketsSold) * 100).toFixed(1)
      : '0';

  const hasReports = (raffle?._count?.reports ?? 0) > 0;

  const handleBuy = useCallback(async () => {
    if (!raffle || !canBuy) return;

    const signature = await signMessage(
      `Confirm purchase of ${quantity} ticket(s) for raffle ${getRaffleDisplayName(raffle)} on Omega One.`,
    );
    if (!signature) return;

    const success = await buyTickets(raffle.id, quantity);
    if (success) {
      notify('Tickets purchased successfully!', 'success');
      fetchRaffle(raffle.id);
      getUserTicketCount(raffle.id).then(setUserTickets);
      setQuantity(1);
    }
  }, [raffle, canBuy, quantity, signMessage, buyTickets, notify, fetchRaffle, getUserTicketCount]);

  const handleAddToCart = useCallback(() => {
    if (!raffle || !isRaffleLive) return;
    addToCart(raffle, quantity);
    notify('Added to cart', 'success');
  }, [raffle, isRaffleLive, quantity, addToCart, notify]);

  const handleToggleFavorite = useCallback(async () => {
    if (!raffle) return;
    if (!isConnected) {
      setWalletModalOpen(true);
      return;
    }
    const result = await toggleFavorite(raffle.id);
    if (result) {
      setIsFavorited(result.favorited);
      notify(result.favorited ? 'Added to favorites' : 'Removed from favorites', 'success');
    }
  }, [raffle, isConnected, toggleFavorite, notify]);

  const handleReport = useCallback(
    async (reason: string): Promise<boolean> => {
      if (!raffle) return false;
      const success = await reportRaffle(raffle.id, reason);
      if (success) {
        notify('Report submitted. Thank you.', 'success');
        fetchRaffle(raffle.id);
      }
      return success;
    },
    [raffle, reportRaffle, notify, fetchRaffle],
  );

  const handleShare = useCallback(() => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    notify('Link copied to clipboard', 'success');
    setTimeout(() => setCopied(false), 2000);
  }, [notify]);

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
    if (tab === 'participants') setParticipantsPage(1);
    else setTransactionsPage(1);
  };

  const truncateId = (str: string) => {
    if (!str || str.length <= 12) return str;
    return `${str.slice(0, 5)}...${str.slice(-3)}`;
  };

  // Loading state
  if (raffleLoading || !raffle) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="text-grayscale-400 text-lg">
          {raffleLoading ? 'Loading raffle...' : 'Raffle not found'}
        </div>
      </div>
    );
  }

  const raffleName = getRaffleDisplayName(raffle);
  const raffleImage = getRaffleImage(raffle);
  const raffleCreator = getRaffleCreator(raffle);
  const ticketPriceBtc = satsToBtc(raffle.ticketPriceSats);
  const totalTicketValueBtc = satsToBtc(raffle.totalTicketValue);
  const floorPriceBtc = totalTicketValueBtc;
  const ttvBtc = ticketPriceBtc * raffle.supply;
  const percentChange =
    floorPriceBtc > 0 ? (((ttvBtc - floorPriceBtc) / floorPriceBtc) * 100).toFixed(2) : '0';

  // Winner info
  const topWinner = winners.length > 0 ? winners[0] : null;

  // Info section (shared between mobile header and desktop)
  const renderInfoSection = () => (
    <div className="space-y-1">
      <div className="flex gap-4 items-center">
        <div className="w-8 h-8 bg-grayscale-600 rounded-full"></div>

        <div className="flex gap-1 items-center text-lg text-brand-magnolia">
          <div>{raffle.ordinalCollectionName || 'Collection'}</div>
          <IconSvg icon="verifiedS" className="text-brand-tangerine" />
        </div>

        <div className="flex gap-1 items-center text-brand-granite">
          <IconSvg icon="discordS" />
          <IconSvg icon="twitterS" />
          <IconSvg icon="webS" />
        </div>
      </div>

      <div className="flex gap-2 items-center">
        <h3 className="text-brand-magnolia leading-[43px] xl:text-[32px] text-[22px]">
          {raffleName}
        </h3>

        <button
          className="p-2 rounded-lg border border-grayscale-700 bg-brand-black text-grayscale-400 relative"
          onClick={handleShare}
        >
          <IconSvg icon="shareS" />
          {copied && (
            <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs bg-grayscale-800 text-brand-magnolia px-2 py-1 rounded whitespace-nowrap">
              Copied!
            </span>
          )}
        </button>
      </div>

      <button
        className="flex gap-1 items-center text-sm text-system-error"
        onClick={() => setReportModalOpen(true)}
      >
        <IconSvg icon="reportS" />
        <span className="underline">Report</span>
      </button>
    </div>
  );

  const renderStatsCards = () => (
    <div className="flex overflow-x-auto gap-3 no-scrollbar xl:flex-wrap">
      <div className="px-4 py-2 min-w-[155px] space-y-1 rounded-lg bg-grayscale-800 flex-shrink-0">
        <div className="text-xss text-grayscale-400">Floor Price</div>
        <div className="flex gap-2 items-center">
          <IconSvg icon="bitcoinS" />
          <span className="text-lg font-medium text-brand-magnolia">
            {floorPriceBtc.toFixed(4)}
          </span>
        </div>
      </div>

      <div className="px-4 py-2 min-w-[155px] space-y-1 rounded-lg bg-grayscale-800 flex-shrink-0">
        <div className="text-xss text-grayscale-400">Total Tickets Value</div>
        <div className="flex gap-2 items-center">
          <IconSvg icon="bitcoinS" />
          <span className="text-lg font-medium text-brand-magnolia">{ttvBtc.toFixed(4)}</span>
        </div>
      </div>

      <div className="px-4 py-2 min-w-[155px] space-y-1 rounded-lg bg-grayscale-800 flex-shrink-0">
        <div className="text-xss text-grayscale-400">% change (TTV-FP)</div>
        <span
          className={`font-medium text-lg tracking-[-0.1px] ${
            Number(percentChange) >= 0 ? 'text-system-success' : 'text-system-error'
          }`}
        >
          {Number(percentChange) >= 0 ? '+' : ''}
          {percentChange}%
        </span>
      </div>
    </div>
  );

  const renderWinnerCard = () => {
    if (!topWinner) return null;
    const isUserWinner = userId === topWinner.winnerId;

    return (
      <div className="p-6 rounded-lg bg-[#FF991A] text-brand-black">
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="text-4xl">🏆</div>
          <h3 className="text-[24px] font-bold">Winner!</h3>

          <div className="flex gap-3 items-center">
            <div className="w-10 h-10 bg-brand-black/20 rounded-full"></div>
            <div className="text-lg font-medium">@{truncateId(topWinner.winnerId)}</div>
          </div>

          <div className="text-sm">Tickets Bought: {topWinner.winningTicketNumber}</div>

          {isUserWinner && topWinner.claimStatus === 'PENDING' && (
            <Button variant="ghost" size="sm" className="mt-2">
              Claim Prize
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderPurchaseSection = () => {
    if (isRaffleEnded && topWinner) {
      return renderWinnerCard();
    }

    if (isAwaitingWinner) {
      return (
        <div className="overflow-hidden relative p-6 w-full rounded-lg bg-grayscale-800">
          <div className="flex flex-col items-center text-center space-y-4 py-6">
            <IconSvg icon="liveS" className="text-brand-tangerine w-8 h-8" />
            <h4 className="text-brand-magnolia text-lg font-semibold">Raffle Ended</h4>
            <p className="text-grayscale-400 text-sm">
              This raffle has ended. Waiting for the creator to select the winner.
            </p>
            {isCreator && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate(`/test/winner`)}
              >
                Select Winner
              </Button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="overflow-hidden relative p-3 w-full rounded-lg xl:p-6 xl:space-y-6 bg-grayscale-800">
        <div className="mb-4 space-y-2">
          <div className="flex justify-between items-center text-sm text-grayscale-300">
            <div>Quantity</div>
            <div>Max: {maxQuantity > 0 ? maxQuantity.toLocaleString() : 0}</div>
          </div>

          <NumberInputWithMultiplier
            className="w-full h-[48px]"
            value={quantity}
            onChange={(val: number) => setQuantity(Math.min(val, Math.max(maxQuantity, 1)))}
            min={1}
            max={Math.max(maxQuantity, 1)}
          />
        </div>

        <div className="mb-4 space-y-2">
          <div className="flex justify-between items-center text-xss xl:text-sm text-grayscale-300">
            <div>
              {isCreator && (
                <span className="text-system-error">You are the creator of this raffle</span>
              )}
              {!isRaffleLive && !isCreator && (
                <span className="text-system-error">Raffle is not live</span>
              )}
              {isRaffleLive && !isCreator && maxQuantity <= 0 && (
                <span className="text-system-error">
                  {raffle.ticketLimitPerWallet ? 'Ticket limit reached' : 'Sold out'}
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="primary"
              size={isMobile ? 'xs' : 'md'}
              className="flex-1"
              onClick={handleBuy}
              disabled={!canBuy || actionLoading}
            >
              {actionLoading ? 'Processing...' : `Buy • ${totalBtc.toFixed(4)} BTC`}
            </Button>

            <IconButton
              variant="dark"
              size={isMobile ? 'xs' : 'md'}
              icon={isMobile ? 'shoppingAddS' : 'shoppingAddM'}
              className="flex-1"
              onClick={handleAddToCart}
              disabled={!isRaffleLive || isCreator}
            >
              Add to Cart
            </IconButton>
          </div>
        </div>

        <div className="flex gap-1 items-center text-sm xl:text-base">
          <div className="text-grayscale-300">Need BTC?</div>
          <button className="underline text-brand-tangerine">Swap on Xverse</button>
        </div>

        {!isConnected && (
          <div className="absolute inset-0 bg-[#151517A3] backdrop-blur-sm flex items-center justify-center !mt-0">
            <Button variant="secondary" size="sm" onClick={() => setWalletModalOpen(true)}>
              Connect Wallet
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="mb-6 xl:mb-8">
        <IconButton
          size={isMobile ? 'xs' : 'sm'}
          variant="transparent"
          icon="arrowLeftS"
          onClick={goBack}
        >
          Go Back
        </IconButton>
      </div>

      <section className="flex flex-col gap-6 mb-6 xl:mb-12 xl:gap-7 xl:flex-row">
        {/* Mobile header */}
        <div className="xl:hidden">
          {hasReports && (
            <div className="flex gap-2 items-center px-4 py-2 mb-6 w-full rounded-lg border bg-grayscale-800 border-system-error">
              <IconSvg icon="reportM" className="flex-shrink-0 text-system-error" />
              <div className="text-sm text-grayscale-300">
                This raffle and/or creator has been flagged for suspicious activity. Please buy at
                your own risk
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-x-1 gap-y-3 justify-between items-end">
            {renderInfoSection()}
            {renderStatsCards()}
          </div>
        </div>

        {/* Left panel - Image + Traits/Details */}
        <div className="w-full xl:max-w-[698px]">
          <div className="overflow-hidden relative mb-3 w-full h-auto rounded-lg xl:mb-4">
            <button
              className={`absolute bg-brand-black rounded-lg p-2.5 flex items-center justify-center top-5 right-5 z-10 ${
                isFavorited ? 'text-brand-tangerine' : 'text-grayscale-400'
              }`}
              onClick={handleToggleFavorite}
            >
              <IconSvg icon="starM" />
            </button>

            <img src={raffleImage} alt={raffleName} className="object-cover w-full h-full" />

            <div className="absolute bottom-3 right-5 left-5 gap-2 items-center px-3 py-2 rounded-lg xl:space-y-2 xl:px-6 xl:py-3 bg-grayscale-800 text-grayscale-300">
              <div className="flex justify-between items-center text-xss xs:text-sm text-grayscale-300">
                <div>Raffle Creator</div>
                <div>Total Live/Finished Raffles</div>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex gap-2 items-center">
                  <div className="w-4 h-4 bg-grayscale-600 rounded-full xl:w-6 xl:h-6"></div>
                  <h5 className="!text-sm text-brand-magnolia xl:text-lg">@{raffleCreator}</h5>
                  <IconSvg icon={isMobile ? 'twitterS' : 'twitterM'} />
                </div>

                <div className="xl:text-lg text-grayscale-200">-/-</div>
              </div>
            </div>
          </div>

          <div className="space-y-2 xl:space-y-4">
            {/* Traits - only for ORDINALS */}
            {raffle.prizeType === 'ORDINALS' && (
              <Disclosure as="div" className="p-3 w-full rounded-lg xl:p-6 bg-grayscale-800">
                <DisclosureButton className="flex justify-between items-center w-full group">
                  <div className="text-lg xl:!text-[24px] xl:!font-medium text-grayscale-200 !font-normal xl:text-grayscale-300 !text-start">
                    Traits
                  </div>
                  <IconSvg
                    icon="arrowDownM"
                    className="text-grayscale-300 group-data-[open]:rotate-180 transition-all duration-200"
                  />
                </DisclosureButton>

                <DisclosurePanel className="!mt-3 text-grayscale-400 transition-all duration-200 space-y-3">
                  <div className="text-sm text-grayscale-400">
                    Trait data not available for this ordinal.
                  </div>
                </DisclosurePanel>
              </Disclosure>
            )}

            {/* Details */}
            <Disclosure as="div" className="p-3 w-full rounded-lg xl:p-6 bg-grayscale-800">
              <DisclosureButton className="flex justify-between items-center w-full group">
                <div className="text-lg xl:!text-[24px] xl:!font-medium text-grayscale-200 !font-normal xl:text-grayscale-300 !text-start">
                  Details
                </div>
                <IconSvg
                  icon="arrowDownM"
                  className="text-grayscale-300 group-data-[open]:rotate-180 transition-all duration-200"
                />
              </DisclosureButton>

              <DisclosurePanel className="!mt-2 text-grayscale-400 transition-all duration-200 space-y-3">
                {raffle.ordinalId && (
                  <div className="flex justify-between items-center text-sm xl:text-lg">
                    <div className="text-grayscale-400">Inscription ID</div>
                    <div className="flex gap-2 items-center text-grayscale-400">
                      <button
                        onClick={() =>
                          window.open(
                            `https://ordinals.com/inscription/${raffle.ordinalId}`,
                            '_blank',
                          )
                        }
                      >
                        <IconSvg icon="externalLinkS" />
                      </button>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(raffle.ordinalId || '');
                          notify('Copied', 'success');
                        }}
                      >
                        <IconSvg icon="copyS" />
                      </button>
                      <div className="text-brand-magnolia">{truncateId(raffle.ordinalId)}</div>
                    </div>
                  </div>
                )}

                {raffle.ordinalInscriptionNumber && (
                  <div className="flex justify-between items-center text-sm xl:text-lg">
                    <div className="text-grayscale-400">Inscription Number</div>
                    <div className="text-brand-magnolia">{raffle.ordinalInscriptionNumber}</div>
                  </div>
                )}

                <div className="flex justify-between items-center text-sm xl:text-lg">
                  <div className="text-grayscale-400">Owner</div>
                  <div className="flex gap-2 items-center text-grayscale-400">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(raffle.creatorId);
                        notify('Copied', 'success');
                      }}
                    >
                      <IconSvg icon="copyS" />
                    </button>
                    <div className="text-brand-magnolia">{truncateId(raffle.creatorId)}</div>
                  </div>
                </div>

                {raffle.ordinalContentType && (
                  <div className="flex justify-between items-center text-sm xl:text-lg">
                    <div className="text-grayscale-400">Content Type</div>
                    <div className="text-brand-magnolia">{raffle.ordinalContentType}</div>
                  </div>
                )}

                <div className="flex justify-between items-center text-sm xl:text-lg">
                  <div className="text-grayscale-400">Created</div>
                  <div className="text-brand-magnolia">
                    {new Date(raffle.createdAt).toLocaleString()}
                  </div>
                </div>
              </DisclosurePanel>
            </Disclosure>
          </div>
        </div>

        {/* Right panel */}
        <div className="w-full">
          {!isMobile && (
            <>
              {hasReports && (
                <div className="hidden gap-2 items-center px-4 py-2 mb-6 w-full rounded-lg border xl:flex bg-grayscale-800 border-system-error">
                  <IconSvg icon="reportM" className="text-system-error" />
                  <div className="text-sm text-grayscale-300">
                    This raffle and/or creator has been flagged for suspicious activity. Please buy
                    at your own risk
                  </div>
                </div>
              )}

              <div className="hidden flex-wrap gap-x-1 gap-y-3 justify-between items-end mb-6 xl:flex 3xl:mb-12">
                {renderInfoSection()}
                {renderStatsCards()}
              </div>
            </>
          )}

          <div className="grid grid-cols-1 gap-3 xl:gap-6 mb-6 xl:grid-cols-[repeat(auto-fit,minmax(481px,1fr))]">
            {/* Stats grid */}
            <div className="order-3 p-3 w-full rounded-lg bg-grayscale-800 xl:p-6 xl:order-1">
              <div className="grid grid-cols-2 gap-x-6 gap-y-8 justify-between">
                <div>
                  <div className="text-xs xl:text-sm text-grayscale-400">Ticket Price</div>
                  <div className="flex gap-2 items-center">
                    <IconSvg icon="bitcoinM" />
                    <span className="text-brand-magnolia font-medium text-lg xl:text-[24px] tracking-[-0.1px]">
                      {ticketPriceBtc.toFixed(4)}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="text-xs xl:text-sm text-grayscale-400">Time Remaining</div>
                  <div className="text-brand-magnolia font-medium text-lg xl:text-[24px] tracking-[-0.1px]">
                    {timeLeft}
                  </div>
                  {raffle.startedAt && (
                    <div className="text-xss xl:text-xs text-grayscale-400">
                      Raffle Started:{' '}
                      {new Date(raffle.startedAt).toLocaleDateString('en-US', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-xs xl:text-sm text-grayscale-400">Remaining Tickets</div>
                  <div className="text-brand-magnolia font-medium text-lg xl:text-[24px] tracking-[-0.1px]">
                    {formatTicketsSold(raffle)}
                  </div>
                  {isConnected && (
                    <div className="text-xss xl:text-xs text-grayscale-400">
                      Your tickets: {userTickets} •
                      <span className="text-system-success"> {probability}% chance</span>
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-xs xl:text-sm text-grayscale-400">Winners</div>
                  <div className="text-brand-magnolia font-medium text-lg xl:text-[24px] tracking-[-0.1px]">
                    {raffle.numberOfWinners}
                  </div>
                </div>
              </div>
            </div>

            {/* Mempool protection - mobile */}
            <div className="order-2 p-3 rounded-lg xl:p-6 bg-grayscale-800 xl:hidden">
              <div className="flex gap-3 items-center">
                <ToggleButton enabled={enabled} setEnabled={setEnabled} />
                <div className="flex gap-2 items-center">
                  <div className="xl:text-lg text-grayscale-300">
                    Add Partial Mempool Protection
                  </div>
                  <IconSvg icon="infoS" className="text-grayscale-400" />
                </div>
              </div>

              {enabled && (
                <>
                  <div className="mt-6 mb-2 space-y-2">
                    <div className="flex justify-between items-end">
                      <div className="xl:text-lg text-brand-magnolia">Max Network Fee</div>
                      <div className="text-sm text-grayscale-400">2x base</div>
                    </div>
                    <NumberInputWithMultiplier size="md" className="w-full" />
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-sm text-center text-grayscale-400">
                      <div>First Fee</div>
                      <div className="text-base font-semibold text-brand-magnolia">6 sats/vB</div>
                      <div>~ $2.82</div>
                    </div>
                    <div className="text-sm text-center text-grayscale-400">
                      <div>Second Fee</div>
                      <div className="text-base font-semibold text-brand-magnolia">6 sats/vB</div>
                      <div>~ $2.82</div>
                    </div>
                    <div className="text-sm text-center text-grayscale-400">
                      <div>Max Fee</div>
                      <div className="text-base font-semibold text-brand-magnolia">6 sats/vB</div>
                      <div>~ $2.82</div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Purchase section / Winner card */}
            <div className="order-1 xl:order-2">{renderPurchaseSection()}</div>
          </div>

          {/* Mempool protection - desktop */}
          {!isMobile && (
            <div className="hidden p-3 rounded-lg xl:p-6 bg-grayscale-800 xl:block mb-6">
              <div className="flex gap-3 items-center mb-6">
                <ToggleButton enabled={enabled} setEnabled={setEnabled} />
                <div className="text-lg text-grayscale-300">Add Partial Mempool Protection</div>
                <IconSvg icon="infoS" className="text-grayscale-400" />
              </div>

              {enabled && (
                <>
                  <div className="mb-2 space-y-2">
                    <div className="flex justify-between items-end">
                      <div className="xl:text-lg text-brand-magnolia">Max Network Fee</div>
                      <div className="text-sm text-grayscale-400">2x base</div>
                    </div>
                    <NumberInputWithMultiplier size="md" className="w-full" />
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-sm text-center text-grayscale-400">
                      <div>First Fee</div>
                      <div className="text-base font-semibold text-brand-magnolia">6 sats/vB</div>
                      <div>~ $2.82</div>
                    </div>
                    <div className="text-sm text-center text-grayscale-400">
                      <div>Second Fee</div>
                      <div className="text-base font-semibold text-brand-magnolia">6 sats/vB</div>
                      <div>~ $2.82</div>
                    </div>
                    <div className="text-sm text-center text-grayscale-400">
                      <div>Max Fee</div>
                      <div className="text-base font-semibold text-brand-magnolia">6 sats/vB</div>
                      <div>~ $2.82</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="hidden my-6 w-full border-t border-grayscale-600 xl:block"></div>

          {/* Participants / Transactions tabs */}
          <div>
            <div className="flex gap-3 items-center mb-2">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  className={`flex gap-2 items-center py-2 px-3 rounded-lg font-medium ${
                    activeTab === tab.key
                      ? 'bg-grayscale-800 text-grayscale-100'
                      : 'text-grayscale-300'
                  }`}
                  onClick={() => handleTabClick(tab.key)}
                >
                  <IconSvg icon={tab.icon} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            <div className="relative">
              <div className="overflow-x-auto p-3 px-5 rounded-lg bg-grayscale-800 custom-scrollbar">
                <div className="min-w-[750px]">
                  {activeTab === 'participants' ? (
                    <>
                      <div className="grid grid-cols-[1fr_1fr] py-2 text-sm text-grayscale-300">
                        <div>User</div>
                        <div>Tickets Bought</div>
                      </div>

                      <div className="max-h-[500px] overflow-y-auto custom-scrollbar">
                        {isLoadingParticipants ? (
                          <div className="py-8 text-center text-grayscale-400">Loading...</div>
                        ) : participants.length === 0 ? (
                          <div className="py-8 text-center text-grayscale-400">
                            No participants yet
                          </div>
                        ) : (
                          participants.map((p, index) => (
                            <div
                              key={index}
                              className="grid grid-cols-[1fr_1fr] py-3 text-lg font-medium text-brand-magnolia"
                            >
                              <div>{truncateId(p.buyerId)}</div>
                              <div>{p._sum.quantity}</div>
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="grid grid-cols-[1fr_1fr_1fr_188px] py-2 text-sm text-grayscale-300">
                        <div>Transaction</div>
                        <div>Buyer</div>
                        <div className="flex gap-1 items-center">
                          <span>Date & Time</span>
                          <IconSvg icon="trianglesS" className="text-grayscale-300" />
                        </div>
                        <div className="flex gap-1 items-center">
                          <span>Ticket</span>
                          <IconSvg icon="trianglesS" className="text-grayscale-300" />
                        </div>
                      </div>

                      <div className="max-h-[500px] overflow-y-auto custom-scrollbar">
                        {isLoadingTransactions ? (
                          <div className="py-8 text-center text-grayscale-400">Loading...</div>
                        ) : transactions.length === 0 ? (
                          <div className="py-8 text-center text-grayscale-400">
                            No transactions yet
                          </div>
                        ) : (
                          transactions.map(tx => (
                            <div
                              key={tx.id}
                              className="grid grid-cols-[1fr_1fr_1fr_188px] py-3 text-lg font-medium text-brand-magnolia"
                            >
                              <div className="flex gap-1 items-center font-semibold">
                                <span>{truncateId(tx.id)}</span>
                                <IconSvg icon="externalLinkS" className="text-grayscale-300" />
                              </div>
                              <div>{truncateId(tx.buyerId)}</div>
                              <div>
                                {new Date(tx.createdAt).toLocaleDateString('en-US', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: '2-digit',
                                })}{' '}
                                •{' '}
                                {new Date(tx.createdAt).toLocaleTimeString('en-US', {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true,
                                })}
                              </div>
                              <div>{tx.quantity}</div>
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="absolute top-0 right-0 bottom-0 w-16 bg-gradient-to-l to-transparent pointer-events-none from-brand-black md:hidden"></div>
            </div>

            {/* Pagination */}
            {activeTab === 'participants' &&
              participantsMeta &&
              participantsMeta.totalPages > 1 && (
                <Pagination
                  currentPage={participantsPage}
                  totalPages={participantsMeta.totalPages}
                  onPageChange={setParticipantsPage}
                />
              )}
            {activeTab === 'transactions' &&
              transactionsMeta &&
              transactionsMeta.totalPages > 1 && (
                <Pagination
                  currentPage={transactionsPage}
                  totalPages={transactionsMeta.totalPages}
                  onPageChange={setTransactionsPage}
                />
              )}
          </div>
        </div>
      </section>

      {/* You Might Also Love */}
      {displayRecommended.length > 0 && (
        <section>
          <h3 className="mb-8 text-brand-magnolia">You Might Also Love</h3>

          <div className="grid gap-[18px] grid-cols-[repeat(auto-fit,minmax(320px,1fr))]">
            {displayRecommended.map(r => (
              <RaffleCard
                key={r.id}
                image={getRaffleImage(r)}
                title={getRaffleDisplayName(r)}
                creator={getRaffleCreator(r)}
                ticketsSold={formatTicketsSold(r)}
                ticketsPrice={satsToBtc(r.ticketPriceSats)}
                endDate={r.endDate}
                onClick={() => navigate(`/raffle/${r.id}`)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Modals */}
      <SelectWalletModal isOpen={walletModalOpen} close={() => setWalletModalOpen(false)} />
      <ReportRaffleModal
        isOpen={reportModalOpen}
        close={() => setReportModalOpen(false)}
        onSubmit={handleReport}
      />
    </>
  );
};

export default JoinRaffle;
